import {
  Button,
  ChakraSelect,
  FormControl,
  FormLabel,
  HStack,
  Stack,
  useBoolean,
  useDisclosure,
} from '@elkaso-app/web-design';
import { BaseFilterButton } from 'components/base-table/filters/base-filter-button';
import { usePageParams } from 'hooks/use-page-params';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TSelectOption = { value: string; label: string };
type TInputs = {
  isVerified?: TSelectOption | null;
};

const defaultValues: TInputs = {
  isVerified: null,
};

const verificationOptions: TSelectOption[] = [
  { value: 'true', label: 'Verified' },
  { value: 'false', label: 'Unverified' },
];

export const VerificationStatusFilterButton = () => {
  const [isActive, setIsActive] = useBoolean(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();

  // Update active filter button
  useEffect(() => {
    if (params.isVerified !== undefined && params.isVerified !== '') setIsActive.on();
    else setIsActive.off();
  }, [params.isVerified, setIsActive]);

  const { control, handleSubmit, formState, reset, setValue } = useForm<TInputs>({
    defaultValues: { ...defaultValues },
  });

  // Initialize form values from URL params
  useEffect(() => {
    const isVerifiedParam =
      params.isVerified !== undefined && params.isVerified !== null && params.isVerified !== ''
        ? verificationOptions.find((opt) => opt.value === String(params.isVerified)) || null
        : null;
    setValue('isVerified', isVerifiedParam);
  }, [params.isVerified, setValue]);

  const onSubmit = (data: TInputs) => {
    const newParams = { ...params };

    if (data.isVerified && data.isVerified.value) {
      newParams.isVerified = data.isVerified.value;
    } else {
      delete newParams.isVerified;
    }

    setPageParams(newParams);
    onClose();
    reset(data);
  };

  const handleClose = () => {
    onClose();
    // Reset form to current params
    const isVerifiedParam =
      params.isVerified !== undefined && params.isVerified !== null && params.isVerified !== ''
        ? verificationOptions.find((opt) => opt.value === String(params.isVerified)) || null
        : null;
    setValue('isVerified', isVerifiedParam);
  };

  const handleClear = () => {
    setValue('isVerified', null);

    const newParams = { ...params };
    delete newParams.isVerified;
    setPageParams(newParams);

    onClose();
  };

  const handleDisableFilterButton = () => {
    const isDisabled = Object.keys(formState.dirtyFields).length ? false : true;
    return isDisabled;
  };

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={isActive}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleSubmit(onSubmit)}>
          <FormControl>
            <FormLabel>Verification Status</FormLabel>
            <Controller
              name='isVerified'
              control={control}
              render={({ field }) => (
                <ChakraSelect
                  {...field}
                  placeholder='Select verification status...'
                  options={verificationOptions}
                  isClearable
                  onChange={(option) => field.onChange(option)}
                />
              )}
            />
          </FormControl>

          <HStack justify='flex-end'>
            <Button variant='outline' colorScheme='gray' onClick={handleClear}>
              Clear
            </Button>
            <Button variant='outline' colorScheme='red' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='red' type='submit' disabled={handleDisableFilterButton()}>
              Filter
            </Button>
          </HStack>
        </Stack>
      }
    />
  );
};
